import {
  Box,
  CircularProgress,
  Pagination,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { FormEvent, RefObject } from 'react';

import { LIBRARY_LAYOUT_SIZES } from '@mightyhive/theme';

import type { ManagedClient } from '../clientsUtils';
import { getClientsPageTokens } from '../getClientsPageTokens';
import { CLIENTS_PAGE_LAYOUT_SIZES } from '../clientsTokens';
import { AddClientForm } from './AddClientForm';
import { ClientListItem } from './ClientListItem';
import { ClientsSearchField } from './ClientsSearchField';

export interface ClientListPanelProps {
  clientListPanelRef: RefObject<HTMLDivElement | null>;
  clientListToolbarRef: RefObject<HTMLDivElement | null>;
  clientName: string;
  clientPage: number;
  clientPageCount: number;
  clientSearch: string;
  clients: ManagedClient[];
  filteredClientsCount: number;
  isPoweruser: boolean;
  loadingClients: boolean;
  onClientNameChange: (value: string) => void;
  onClientPageChange: (page: number) => void;
  onClientSearchChange: (value: string) => void;
  onCreateClient: (event: FormEvent) => void;
  onSelectClient: (uuid: string) => void;
  pagedClients: ManagedClient[];
  saving: boolean;
  selectedClientUuid: string | null;
}

export function ClientListPanel({
  clientListPanelRef,
  clientListToolbarRef,
  clientName,
  clientPage,
  clientPageCount,
  clientSearch,
  clients,
  filteredClientsCount,
  isPoweruser,
  loadingClients,
  onClientNameChange,
  onClientPageChange,
  onClientSearchChange,
  onCreateClient,
  onSelectClient,
  pagedClients,
  saving,
  selectedClientUuid,
}: ClientListPanelProps): JSX.Element {
  const theme = useTheme();
  const tokens = getClientsPageTokens(theme);

  return (
    <Box
      ref={clientListPanelRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        borderRadius: `${LIBRARY_LAYOUT_SIZES.sectionRadius}px`,
        border: `1px solid ${tokens.sectionSurfaceBorder}`,
        bgcolor: tokens.sectionSurfaceBg,
        overflow: 'hidden',
      }}
    >
      <Box ref={clientListToolbarRef} sx={{ flexShrink: 0 }}>
        <Stack spacing={2} sx={{ p: `${CLIENTS_PAGE_LAYOUT_SIZES.panelPadding}px` }}>
          {isPoweruser ? (
            <AddClientForm
              clientName={clientName}
              disabled={saving}
              onClientNameChange={onClientNameChange}
              onSubmit={onCreateClient}
            />
          ) : null}
          <ClientsSearchField
            placeholder="Search clients"
            value={clientSearch}
            onChange={(value) => {
              onClientSearchChange(value);
              onClientPageChange(1);
            }}
          />
        </Stack>
      </Box>

      {loadingClients ? (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', flex: 1 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              flex: 1,
              minHeight: 120,
              overflowY: 'auto',
              px: 1,
              pb: 1,
            }}
          >
            {pagedClients.map((client) => (
              <ClientListItem
                key={client.externalUuid}
                name={client.name}
                memberCount={client.memberCount}
                isPersonal={client.isPersonal}
                isSelected={client.externalUuid === selectedClientUuid}
                onSelect={() => onSelectClient(client.externalUuid)}
              />
            ))}
            {filteredClientsCount === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ color: tokens.bodySecondaryColor }}>
                  {clients.length === 0 ? 'No clients found.' : 'No clients match your search.'}
                </Typography>
              </Box>
            ) : null}
          </Box>

          {clientPageCount > 1 ? (
            <Box
              sx={{
                p: 1,
                display: 'flex',
                justifyContent: 'center',
                flexShrink: 0,
                borderTop: `1px solid ${tokens.sectionSurfaceBorder}`,
              }}
            >
              <Pagination
                count={clientPageCount}
                page={clientPage}
                onChange={(_event, value) => onClientPageChange(value)}
                size="small"
              />
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
}
